import React, { useState } from 'react';
import {
  Box,
  Typography,
  Tooltip,
  LinearProgress,
  Skeleton,
  ClickAwayListener,
} from '@mui/material';
import { HelpOutline as HelpOutlineIcon } from '@mui/icons-material';

const AiUsageStats = ({ usageStats, loading, fullWidth = false, sx }) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);

  if (!loading && !usageStats) return null;

  const containerSx = {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.5,
    backgroundColor: 'rgba(255, 255, 255, 0.07)',
    borderRadius: '6px',
    px: 1.25,
    py: 0.6,
    width: fullWidth ? '100%' : 'auto',
    minWidth: fullWidth ? 0 : 180,
    boxSizing: 'border-box',
    ...sx,
  };

  if (loading) {
    return (
      <Box
        sx={{
          ...containerSx,
          '& .MuiSkeleton-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.18)',
          },
          '& .MuiSkeleton-root::after': {
            background:
              'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.35), transparent)',
          },
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Skeleton
            variant="text"
            animation="wave"
            width={36}
            sx={{ fontSize: '10px', lineHeight: 1 }}
          />
          <Box sx={{ flex: 1 }} />
          <Skeleton
            variant="text"
            animation="wave"
            width={40}
            sx={{ fontSize: '12px', lineHeight: 1 }}
          />
          <Skeleton variant="circular" animation="wave" width={14} height={14} />
        </Box>
        <Skeleton
          variant="rectangular"
          animation="wave"
          height={4}
          sx={{ borderRadius: 2 }}
        />
      </Box>
    );
  }

  const usagePercent = usageStats.limit > 0
    ? Math.min(100, (usageStats.used / usageStats.limit) * 100)
    : 0;
  const barColor = usageStats.remaining <= 10
    ? 'error.light'
    : usageStats.remaining <= 30
      ? 'warning.light'
      : 'rgba(255, 255, 255, 0.9)';

  return (
    <Box sx={containerSx}>
      <Box display="flex" alignItems="center" gap={1}>
        <Typography
          variant="caption"
          sx={{
            fontSize: '9px',
            fontWeight: 700,
            letterSpacing: '0.08em',
            color: 'rgba(255, 255, 255, 0.8)',
            lineHeight: 1,
          }}
        >
          Usage
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Typography
          variant="caption"
          sx={{
            fontSize: '10px',
            fontWeight: 600,
            color: 'white',
            lineHeight: 1,
          }}
        >
          {usageStats.used} / {usageStats.limit}
        </Typography>
        <ClickAwayListener onClickAway={() => setTooltipOpen(false)}>
          <Tooltip
            title={(
              <Typography
                variant="caption"
                sx={{ display: 'block', fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.9)' }}
              >
                {usageStats.remaining} prompts remaining • Limits reset monthly
              </Typography>
            )}
            arrow
            open={tooltipOpen}
            onClose={() => setTooltipOpen(false)}
            onOpen={() => setTooltipOpen(true)}
            disableFocusListener
          >
            <HelpOutlineIcon
              onClick={() => setTooltipOpen((prev) => !prev)}
              sx={{
                fontSize: '12px',
                color: 'rgba(255, 255, 255, 0.85)',
                cursor: 'pointer',
              }}
            />
          </Tooltip>
        </ClickAwayListener>
      </Box>
      <LinearProgress
        variant="determinate"
        value={usagePercent}
        sx={{
          height: 4,
          borderRadius: 2,
          backgroundColor: 'rgba(255, 255, 255, 0.2)',
          '& .MuiLinearProgress-bar': {
            backgroundColor: barColor,
            borderRadius: 2,
          },
        }}
      />
    </Box>
  );
};

export default AiUsageStats;
